import { useState } from 'react';
import { HbsModal } from './hbs/Modal';
import { HbsButton } from './hbs/Button';
import { HbsInput } from './hbs/Input';
import { HbsStrengthMeter } from './hbs/StrengthMeter';
import { EyeToggle } from './hbs/EyeToggle';
import { SafePasswordTipsModal } from './SafePasswordTipsModal';
import { passwordStrength, MIN_PASSWORD_LENGTH } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps): JSX.Element | null {
  const { changeSafePassword } = useSafe();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [revealCurrent, setRevealCurrent] = useState(false);
  const [revealNext, setRevealNext] = useState(false);
  // Strong-password tips: pops once on first focus of the New password
  // field, re-armed each time the modal is opened (reset() runs on close).
  const [tipsOpen, setTipsOpen] = useState(false);
  const [tipsDismissed, setTipsDismissed] = useState(false);

  function reset(): void {
    setCurrent('');
    setNext('');
    setConfirm('');
    setBusy(false);
    setError('');
    setDone(false);
    setRevealCurrent(false);
    setRevealNext(false);
    setTipsOpen(false);
    setTipsDismissed(false);
  }

  function handleClose(): void {
    if (busy) return; // never abandon a ceremony mid-flight
    reset();
    onClose();
  }

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit =
    current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === confirm && !busy;

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const res = await changeSafePassword(current, next);
      if (res.ok) {
        setDone(true);
      } else {
        setError(res.error ?? 'Could not change your safe password');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <HbsModal
        open={open}
        onClose={handleClose}
        title="Change safe password"
        size="md"
        dismissOnBackdrop={!busy && !tipsOpen}
        dismissOnEsc={!busy && !tipsOpen}
      >
        {done ? (
          <>
            <p>Your safe password has been changed.</p>
            <p className="text-hbs-text-mute">
              For your security, every other device has been signed out. Sign in
              again on those devices with your new password.
            </p>
            <div className="mt-6 flex justify-end">
              <HbsButton id="change-password-done-button" onClick={handleClose}>Done</HbsButton>
            </div>
          </>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }} className="space-y-3">
            <HbsInput
              type={revealCurrent ? 'text' : 'password'}
              label="Current password"
              value={current}
              onChange={setCurrent}
              mono={revealCurrent}
              autoFocus
              trailingActions={<EyeToggle revealed={revealCurrent} onChange={setRevealCurrent} />}
            />
            <HbsInput
              type={revealNext ? 'text' : 'password'}
              label="New password"
              value={next}
              onChange={setNext}
              mono={revealNext}
              error={tooShort ? `Use at least ${MIN_PASSWORD_LENGTH} characters` : undefined}
              onFocus={() => {
                if (!tipsDismissed) setTipsOpen(true);
              }}
              trailingActions={<EyeToggle revealed={revealNext} onChange={setRevealNext} />}
            />
            {next.length > 0 && <HbsStrengthMeter strength={passwordStrength(next)} />}
            <HbsInput
              type="password"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              error={mismatch ? 'Passwords do not match' : undefined}
            />

            {error && <p role="alert" className="text-hbs-danger text-[13px]">{error}</p>}

            <div className="mt-6 flex justify-end gap-2">
              <HbsButton type="button" variant="ghost" id="change-password-cancel-button" onClick={handleClose} disabled={busy}>
                Cancel
              </HbsButton>
              <HbsButton id="change-password-submit-button" type="submit" disabled={!canSubmit} loading={busy}>
                Change password
              </HbsButton>
            </div>
          </form>
        )}
      </HbsModal>

      <SafePasswordTipsModal
        open={tipsOpen}
        onClose={() => {
          setTipsOpen(false);
          setTipsDismissed(true);
        }}
      />
    </>
  );
}
